import { TextHighlighter } from '@focaldata/cin-ui-components'
import {
  CfmContext,
  CfmData
} from 'containers/QuestionFlowManager/QuestionFlowManager'
import useEntryProgress from 'hooks/useEntryProgress'
import useEntryRenderingDateTime from 'hooks/useEntryRenderingDateTime'
import { useQuestionnaireParams } from 'hooks/useQuestionnaireParams'
import { useQuestionTimer } from 'hooks/useQuestionTimer'
import useSendTextHighlighterResponse from 'hooks/useSendTextHighlighterResponse'
import {
  QuestionnaireEntry,
  SettingCodes,
  SettingValues,
  TextHighlighterItem
} from 'model/questionnaire'
import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import displayEntityBasedOnLogic from 'utils/displayEntityBasedOnLogic'
import { isAndroid } from 'utils/helper'
import { setHiddenByDisplayLogic } from 'utils/hiddenByDisplayLogic'

interface Highlight {
  id: string
  text: string
  categoryIds: string[]
  comments?: string
}

const TextHighlighterEntry = ({ entry }: { entry: QuestionnaireEntry }) => {
  const { formatMessage } = useIntl()
  const entryItem = entry.entryItem as TextHighlighterItem
  const progress = useEntryProgress(entry)
  const timer = useQuestionTimer(entryItem)
  const { getNextEntryPosition, preview } = useContext<CfmData>(CfmContext)
  const mandatoryParams = useQuestionnaireParams()
  const { sendTextHighlighterResponse } = useSendTextHighlighterResponse()
  const renderingDateTime = useEntryRenderingDateTime(entry)
  const isAndroidDevice = isAndroid()

  const isMultiple =
    entryItem.settingValues?.find(
      (setting) => setting.code === SettingCodes.BasicChoice
    )?.value === SettingValues.MultipleChoice

  const loc = {
    next: formatMessage({
      id: 'question_next',
      defaultMessage: 'Next'
    }),
    instructions: formatMessage({
      id: isAndroidDevice
        ? 'question_textHighlighterInstructionsAndroid'
        : 'question_textHighlighterInstructions',
      defaultMessage: isAndroidDevice
        ? 'Highlight all that apply. Tap your selection after highlighting to choose a category'
        : 'Highlight all that apply'
    }),
    formTitle: formatMessage({
      id: 'question_textHighlighterFormTitle',
      defaultMessage: 'How would you like to highlight this text?'
    }),
    formInstructions: formatMessage({
      id: isMultiple
        ? 'question_textHighlighterMultipleSelectFormInstructions'
        : 'question_textHighlighterSingleSelectFormInstructions',
      defaultMessage: isMultiple
        ? 'Tick all categories that apply'
        : 'Select one category'
    }),
    formCancel: formatMessage({
      id: 'question_textHighlighterFormCancel',
      defaultMessage: 'Cancel'
    }),
    formRemove: formatMessage({
      id: 'question_textHighlighterFormRemove',
      defaultMessage: 'Remove'
    }),
    formSave: formatMessage({
      id: 'question_textHighlighterFormSave',
      defaultMessage: 'Save'
    }),
    commentsPlaceholder: formatMessage({
      id: 'question_textHighlighterCommentsPlaceholder',
      defaultMessage: 'Add a comment (optional)'
    }),
    overlapError: formatMessage({
      id: 'question_textHighlighterOverlapError',
      defaultMessage:
        "You can't highlight text that partially overlaps with another highlight."
    })
  }

  const onNext = (highlights: Highlight[]) => {
    if (!preview) {
      sendTextHighlighterResponse({
        renderedEntryPosition: entry.renderedEntryPosition,
        questionId: entryItem.question.questionId,
        renderedCategoryPositions: entryItem.categories.map(
          (category, index) => ({
            highlighter_category_id: category.option.responseOptionId,
            rendered_highlighter_category_position: index
          })
        ),
        selections: highlights.map((highlight, index) => ({
          selection_number: index,
          highlighter_category_ids: highlight.categoryIds,
          text_selection: highlight.text,
          text_entry: highlight.comments
        })),
        enteringDatetime: renderingDateTime
      })
    }

    getNextEntryPosition()
  }

  const shouldDisplayEntityBasedOnLogic = displayEntityBasedOnLogic(
    mandatoryParams.respondentId,
    mandatoryParams.surveyId,
    entryItem.questionLogic,
    entry.entryType
  )

  // Move to the next question if the current question should not be displayed due to display logic
  if (!shouldDisplayEntityBasedOnLogic) {
    setHiddenByDisplayLogic(
      mandatoryParams.respondentId,
      mandatoryParams.surveyId,
      entryItem.question.questionId
    )
    getNextEntryPosition()
  }

  return (
    <TextHighlighter
      item={entryItem}
      loc={loc}
      next={{ onNext, loading: false }}
      progress={progress}
      preview={preview}
      timer={timer}
    />
  )
}

export default TextHighlighterEntry
